import MapboxDraw, { DrawCustomMode, DrawModeChangeEvent } from "@mapbox/mapbox-gl-draw";
import { Feature, Polygon } from "geojson";
import { defer, uniqBy } from "lodash";
import { LngLatLike } from "mapbox-gl";
import { passing_draw_polygon as passingDrawPolygon } from "mapbox-gl-draw-passing-mode";

import { bbox, booleanContains, booleanIntersects, booleanPointInPolygon } from "fond/turf";
import { isAnyPolygon, isLineString, isPoint } from "fond/types/geojson";

import { isFeatureInEditableScope } from "./helper";

/**
 * Custom draw mode for selecting features by drawing a polygon. This mode extends the passing_draw_polygon mode from mapbox-gl-draw-passing-mode.
 */
const polygonSelect: DrawCustomMode = {
  ...passingDrawPolygon,

  onSetup: function (opts) {
    const state = passingDrawPolygon?.onSetup?.call(this, opts);

    // if no onDraw was set we default to our internal onDraw function that handles selection of features within the drawn polygon
    state.onDraw = opts.onDraw || this.onDraw.bind(this);
    return state;
  },

  onDraw: function (this: MapboxDraw.DrawCustomModeThis, polygon: Feature<Polygon>) {
    if (this.map) {
      // Get the bounding box of the polygon
      // and convert it to a bounding box in pixel coordinates
      // to use with queryRenderedFeatures
      const polygonBounds = bbox(polygon);
      const southWest = [polygonBounds[0], polygonBounds[1]] as LngLatLike;
      const northEast = [polygonBounds[2], polygonBounds[3]] as LngLatLike;
      const northEastPointPixel = this.map.project(northEast);
      const southWestPointPixel = this.map.project(southWest);

      // Get all features within the bounding box
      // and filter them to only include those that are within the polygon
      const features = uniqBy(
        this.map
          ?.queryRenderedFeatures([southWestPointPixel, northEastPointPixel], {
            layers: [
              // Select all features within the drawControl styles
              ...(this._ctx.options.styles?.map(({ id }) => id) || []),
            ],
          })
          .map((f) => {
            // We need to get the feature from the drawControl, we cannot rely on the geometry within the feature
            // returned from the click function above.
            const drawnFeature = this.getFeature(f.properties?.id)?.toGeoJSON() || f;
            return drawnFeature as Feature;
          })
          .filter((f) => {
            if (f.id || f.properties?.id !== undefined) {
              // If editingLayerIds is defined, only select features from editable layers
              if (this._ctx.options.editOptions?.editingLayerIds && "properties" in f) {
                const { editingLayerIds, editableSublayers = [] } = this._ctx.options.editOptions;

                // Use shared helper to check if feature is in editable scope
                if (!isFeatureInEditableScope(f, editingLayerIds, editableSublayers)) {
                  return false;
                }
              }
              // check if feature is within or intersects the polygon
              if (isPoint(f)) {
                return booleanPointInPolygon(f, polygon);
              } else if (isLineString(f) || isAnyPolygon(f)) {
                return booleanContains(polygon, f) || booleanIntersects(f, polygon);
              }
            }
            return false;
          }),
        (f) => f.id ?? f.properties?.id
      );

      defer(() => {
        this.changeMode("simple_select", { featureIds: features.map((f) => f.id ?? f.properties?.id) });
        this.map?.fire("draw.modechange", { type: "draw.modechange", mode: "simple_select" } as DrawModeChangeEvent);
        this.map?.fire("draw.selectionchange", { features });
      });
    }
  },
};

export default polygonSelect;
